import api, { methods } from "@api";
import { useShoppingcartStore } from "@features/cart/shopping-cart-store";
import type { ProductType } from "@type/global";
import { toast } from "@provider/toast";
import type { Account } from "@type/account";
import type { Wishlist } from "@type/wishlist";
import { debounce, normalDebounce } from "@utils";
import { create } from "zustand";
import { useRecipeWishlistStore } from "@features/recipe/favourite/recipe-wishlist-store";

interface WishlistState {
  wishlist: Wishlist[] | null;
  optimisticUpdates: boolean;
  initial: (data: Wishlist[]) => void;
  load: () => void;
  delete: ({ wishlistId }: { wishlistId: number }) => void;
  update: ({ name, wishlistId }: { name: string; wishlistId: number }) => void;
  new: ({ name, product }: { name: string; product: ProductType | undefined }) => void;
  addOrUpdate: ({
    product,
    wishlistId,
    quantity,
  }: {
    product: ProductType;
    wishlistId: number;
    quantity: number;
  }) => void;
  addWishlistToCart: ({ wishlistId }: { wishlistId: number }) => void;

  // addOrUpdateProduct: (product: ProductType, amount: number, token: string) => void;
}

type CreateWishlist = {
  name: string;
  id?: number;
  productPidToLink: number | undefined;
  method?: "delete" | "patch";
};

type UpdateProductInWishlist = {
  user_wishlist: number;
  pid: number;
  quantity: number;
  storeType: string;
};

type WishlistResponse = {
  group: string;
  add_product: boolean;
  message: string;
  wishlist: {
    name: string;
    user_id: number;
    updated_at: string;
    created_at: string;
    id: number;
  };
};

type AddToWishlistResponse = {
  group: string;
  message: string;
  product: {
    user_wishlist: number;
    pid: number;
    user_id: number;
    quantity: number;
    updated_at: string;
    created_at: string;
    id: number;
    product: ProductType;
  };
  wishlist_id: number;
  quantity: number;
};

const getWishlistDebouncer = normalDebounce(() => useWishlistStore.getState().load(), 3000);
const updateWishlistDebounced = () => getWishlistDebouncer();

function crudWishlistDebounced(params: { data: CreateWishlist; alreadyExists: boolean; oldWishlist: Wishlist[] }) {
  const { data, alreadyExists, oldWishlist } = params;
  debounce(
    async () => {
      try {
        const res = await api<WishlistResponse>({
          path: alreadyExists ? `/wishlist/${data.id}` : "/wishlist",
          method: methods.POST,
          body: alreadyExists
            ? { _method: data.method ?? "patch", data: { id: data.id, name: data.name } }
            : { name: data.name, productToLink: data.productPidToLink ?? false },
        });

        // toast.show(res.message);

        return res;
      } catch (err) {
        toast.show((err as any).message);
        useWishlistStore.setState((prev) => ({ ...prev, wishlist: oldWishlist }));
      }
    },
    `wishlist_${data.name}`,
    300
  );
}

function addProductToWishlistDebounced(params: {
  data: UpdateProductInWishlist;
  alreadyInWishlist: boolean;
  oldWishlist: Wishlist[];
}) {
  const { data, alreadyInWishlist, oldWishlist } = params;
  debounce(
    async () => {
      try {
        const res = await api<AddToWishlistResponse>({
          path: alreadyInWishlist ? "/wishlist/product/" + data.pid : "/wishlist/product",
          method: methods.POST,
          body: alreadyInWishlist ? { _method: "patch", data } : { product: data.pid, wishlist: data.user_wishlist },
        });

        // toast.show(res.message);

        return res;
      } catch (err) {
        toast.show((err as any).message);
        useWishlistStore.setState((prev) => ({ ...prev, wishlist: oldWishlist }));
      }
    },
    `product_${data.pid}`,
    300
  );
}

function addWishlistToCart(params: { wishlistId: number }) {
  const { wishlistId } = params;
  debounce(
    async () => {
      try {
        const res = await api<AddToWishlistResponse>({
          path: "/wishlist/product/add-wishlist-to-cart",
          method: methods.POST,
          body: { id: wishlistId },
        });

        useShoppingcartStore.getState().load();

        // toast.show(res.message);

        return res;
      } catch (err) {
        toast.show((err as any).message);
        // useWishlistStore.setState((prev) => ({ ...prev, wishlist: oldWishlist }));
      }
    },
    `wishlist_${wishlistId}`,
    300
  );
}

export const useWishlistStore = create<WishlistState>((set, get) => ({
  wishlist: null,
  optimisticUpdates: false,
  initial: (data) => {
    set({ wishlist: data });
  },
  load: async () => {
    try {
      const response = await api<Account>({
        path: "/account/profile",
        method: methods.GET,
      });
      useRecipeWishlistStore.setState({ recipeWishlist: response.recipe_wishlists });

      set({ wishlist: response.wishlists, optimisticUpdates: false });
    } catch (e) {
      console.log(e);
    }
  },
  delete: ({ wishlistId }) => {
    const oldWishlist = get().wishlist;
    if (oldWishlist === null) {
      console.log("WISHLIST IS NULL");
      return;
    }

    const newWishlist = oldWishlist.filter((list) => list.id !== wishlistId);

    set({ wishlist: newWishlist, optimisticUpdates: true });

    crudWishlistDebounced({
      data: { method: "delete", id: wishlistId, name: "-", productPidToLink: undefined },
      alreadyExists: true,
      oldWishlist,
    });
    updateWishlistDebounced();
  },
  update: ({ name, wishlistId }) => {
    const oldWishlist = get().wishlist;
    if (oldWishlist === null) {
      console.log("WISHLIST IS NULL");
      return;
    }

    const newWishlist = oldWishlist.map((list) => (list.id === wishlistId ? { ...list, name } : list));
    set({ wishlist: newWishlist, optimisticUpdates: true });

    crudWishlistDebounced({
      data: { method: "patch", id: wishlistId, name, productPidToLink: undefined },
      alreadyExists: true,
      oldWishlist,
    });
    updateWishlistDebounced();
  },
  new: ({ name, product }) => {
    const oldWishlist = get().wishlist;
    if (oldWishlist === null) {
      console.log("WISHLIST IS NULL");
      return;
    }

    const maxId = Math.max(...oldWishlist.map((list) => list.id));
    const newWishlist = [
      ...oldWishlist,
      {
        id: maxId + 1,
        name,
        products: product ? [{ id: 0, pid: product.pid, quantity: 1, user_wishlist: 1 }] : [],
      },
    ];

    set({ wishlist: newWishlist, optimisticUpdates: true });

    crudWishlistDebounced({
      data: { name, productPidToLink: product?.pid },
      alreadyExists: false,
      oldWishlist,
    });
    updateWishlistDebounced();
  },
  addOrUpdate: ({ product, wishlistId, quantity }) => {
    const oldWishlist = get().wishlist;
    if (oldWishlist === null) {
      console.log("WISHLIST IS NULL");
      return;
    }

    const productInWhislist = oldWishlist
      .find((list) => list.id === wishlistId)
      ?.products.find((prod) => prod.pid === product.pid);

    const alreadyInWishlist = !!productInWhislist;

    let newWishlist = oldWishlist;
    // already added
    if (quantity === 0) {
      // remove item from list
      newWishlist = oldWishlist.map((list) =>
        list.id === wishlistId
          ? {
              ...list,
              products: list.products.filter((prod) => prod.pid !== product.pid),
            }
          : list
      );
    } else {
      // add item to whislist or update quantity
      newWishlist = oldWishlist.map((list) =>
        list.id === wishlistId
          ? {
              ...list,
              products: alreadyInWishlist
                ? list.products.map((prod) => (prod.pid === product.pid ? { ...prod, quantity } : prod))
                : [
                    ...list.products,
                    {
                      id: Math.max(...list.products.map((prod) => prod.id)) + 1,
                      product,
                      pid: product.pid,
                      quantity: 1,
                    },
                  ],
            }
          : list
      );
    }

    set({ wishlist: newWishlist, optimisticUpdates: true });

    addProductToWishlistDebounced({
      data: { pid: product.pid, user_wishlist: wishlistId, quantity, storeType: "wishlistProducts" },
      alreadyInWishlist,
      oldWishlist,
    });

    updateWishlistDebounced();
  },
  addWishlistToCart: async ({ wishlistId }) => {
    addWishlistToCart({ wishlistId });
    updateWishlistDebounced();
  },
}));

export function useWishlistAmount({ wishlistId, productPid }: { wishlistId: number; productPid: number }) {
  return useWishlistStore((store) => store.wishlist?.find((w) => w.id === wishlistId))?.products.find(
    (prod) => prod.pid === productPid
  )?.quantity;
}
export function useIsInWishlist({ wishlistId, productPid }: { wishlistId: number; productPid: number }) {
  return useWishlistStore((store) => store.wishlist?.find((w) => w.id === wishlistId))?.products.some(
    (prod) => prod.pid === productPid
  );
}

export function useWishlist(wishlistId: number) {
  return useWishlistStore((store) => store.wishlist?.find((w) => w.id === wishlistId));
}

export function useWishlistProductCount(wishlistId: number) {
  return (
    useWishlistStore((store) => store.wishlist?.find((w) => w.id === wishlistId))?.products.reduce(
      (a, b) => a + b.quantity,
      0
    ) ?? 0
  );
}

export function useIsProductInOneOfTheWishlists(productPid: number) {
  return (
    useWishlistStore((store) => store.wishlist?.some((w) => w.products.some((p) => p.pid === productPid))) ?? false
  );
}
